/**
 * What the instructor documents form asks for, and the checks it runs before sending.
 *
 * Mirrors `server/instructor-documents/validation.js`, which has the final say. Field names
 * match the Google Apps Script that files the answers into the compliance spreadsheet.
 */

export const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

export const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];
export const ACCEPT_ATTRIBUTE =
  '.pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic';
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

// Expiry dates further out than this are almost certainly typos.
export const MAX_EXPIRY_YEARS_AHEAD = 20;

export const REQUIREMENT_ALWAYS = 'always';
export const REQUIREMENT_OWN_VEHICLE = 'ownVehicle';
export const REQUIREMENT_OPTIONAL = 'optional';

/**
 * Documents in the order the form shows them. One expiry date can cover several uploads (the
 * licence front and back share the licence expiry).
 */
export const DOCUMENT_GROUPS = [
  {
    key: 'licences',
    documents: [
      {
        key: 'licence',
        uploads: ['licenceFront', 'licenceBack'],
        expiryField: 'licenceExpiry',
        requirement: REQUIREMENT_ALWAYS,
      },
      {
        key: 'authority',
        uploads: ['authority'],
        expiryField: 'authorityExpiry',
        requirement: REQUIREMENT_ALWAYS,
      },
    ],
  },
  {
    key: 'wwcc',
    documents: [
      {
        key: 'wwcc',
        uploads: ['wwcc'],
        expiryField: 'wwccExpiry',
        requirement: REQUIREMENT_ALWAYS,
      },
    ],
  },
  {
    key: 'insurance',
    documents: [
      {
        key: 'publicLiability',
        uploads: ['publicLiability'],
        expiryField: 'publicLiabilityExpiry',
        requirement: REQUIREMENT_ALWAYS,
      },
      {
        key: 'professionalIndemnity',
        uploads: ['professionalIndemnity'],
        expiryField: 'professionalIndemnityExpiry',
        requirement: REQUIREMENT_OPTIONAL,
      },
    ],
  },
  {
    key: 'vehicle',
    documents: [
      {
        key: 'vehicleRego',
        uploads: ['vehicleRego'],
        expiryField: 'vehicleRegoExpiry',
        requirement: REQUIREMENT_OWN_VEHICLE,
      },
      {
        key: 'vehicleInsurance',
        uploads: ['vehicleInsurance'],
        expiryField: 'vehicleInsuranceExpiry',
        requirement: REQUIREMENT_OWN_VEHICLE,
      },
    ],
  },
  {
    key: 'business',
    documents: [
      {
        key: 'trainingQualification',
        uploads: ['trainingQualification'],
        requirement: REQUIREMENT_ALWAYS,
      },
      {
        key: 'abnEvidence',
        uploads: ['abnEvidence'],
        requirement: REQUIREMENT_ALWAYS,
      },
    ],
  },
];

const ALL_DOCUMENTS = DOCUMENT_GROUPS.flatMap(group => group.documents);

/** Is this document asked for, given the answer to "own vehicle"? */
export const documentApplies = (doc, ownVehicle) =>
  doc.requirement !== REQUIREMENT_OWN_VEHICLE || ownVehicle === 'yes';

const pad = n => String(n).padStart(2, '0');

/** Local date as 'YYYY-MM-DD', the value format of <input type="date">. */
export const isoDate = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** min and max for the expiry date inputs. */
export const expiryDateBounds = now => {
  const max = new Date(now.getFullYear() + MAX_EXPIRY_YEARS_AHEAD, now.getMonth(), now.getDate());
  return { min: isoDate(now), max: isoDate(max) };
};

const isRealIsoDate = value => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) {
    return false;
  }
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day;
};

/**
 * Error code for an expiry date, or null.
 *
 * @param {string} value 'YYYY-MM-DD'
 * @param {Date} now
 * @returns {null|'invalid'|'expired'|'tooFar'}
 */
export const expiryDateError = (value, now) => {
  if (!isRealIsoDate(value)) {
    return 'invalid';
  }
  const { min, max } = expiryDateBounds(now);
  if (value < min) {
    return 'expired';
  }
  if (value > max) {
    return 'tooFar';
  }
  return null;
};

/**
 * Error code for a picked file, or null when it can be uploaded.
 *
 * @param {File} file
 * @returns {null|'invalidType'|'empty'|'tooLarge'}
 */
export const fileError = file => {
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return 'invalidType';
  }
  if (!file.size) {
    return 'empty';
  }
  if (file.size > MAX_FILE_BYTES) {
    return 'tooLarge';
  }
  return null;
};

const PHONE_PATTERN = /^\+?[\d\s()-]{8,20}$/;

/**
 * Whole-form validation. Returns error codes keyed by field name; nested for `files`.
 *
 * @param {Object} values form values
 * @param {Date} now
 * @returns {Object} e.g. { phone: 'invalid', files: { wwcc: 'required' } }
 */
export const validateDocumentsForm = (values, now = new Date()) => {
  const errors = {};
  const fileErrors = {};
  const files = values.files || {};

  const fullName = (values.fullName || '').trim();
  if (fullName.length < 2) {
    errors.fullName = 'required';
  }
  const phone = (values.phone || '').trim();
  if (!phone) {
    errors.phone = 'required';
  } else if (!PHONE_PATTERN.test(phone)) {
    errors.phone = 'invalid';
  }
  if (!STATES.includes(values.state)) {
    errors.state = 'required';
  }
  if (values.ownVehicle !== 'yes' && values.ownVehicle !== 'no') {
    errors.ownVehicle = 'required';
  }

  ALL_DOCUMENTS.forEach(doc => {
    if (!documentApplies(doc, values.ownVehicle)) {
      return;
    }
    const expiry = doc.expiryField ? values[doc.expiryField] : null;
    const started = !!expiry || doc.uploads.some(field => files[field]?.fileId);
    const required = doc.requirement !== REQUIREMENT_OPTIONAL || started;
    if (!required) {
      return;
    }
    doc.uploads.forEach(field => {
      if (!files[field]?.fileId) {
        fileErrors[field] = 'required';
      }
    });
    if (doc.expiryField) {
      if (!expiry) {
        errors[doc.expiryField] = 'required';
      } else {
        const dateError = expiryDateError(expiry, now);
        if (dateError) {
          errors[doc.expiryField] = dateError;
        }
      }
    }
  });

  ['ackAgreement', 'ackCodeOfConduct', 'declaration'].forEach(name => {
    if (values[name] !== true) {
      errors[name] = 'required';
    }
  });

  if (Object.keys(fileErrors).length > 0) {
    errors.files = fileErrors;
  }
  return errors;
};

/**
 * What the server receives: file IDs instead of upload state, a boolean for own vehicle, and
 * nothing for documents that do not apply.
 *
 * @param {Object} values form values
 * @returns {Object} request body for /api/instructor-documents/submit
 */
export const submissionBody = values => {
  const ownVehicle = values.ownVehicle === 'yes';
  const body = {
    fullName: (values.fullName || '').trim(),
    phone: (values.phone || '').trim(),
    state: values.state,
    ownVehicle,
    files: {},
    ackAgreement: values.ackAgreement === true,
    ackCodeOfConduct: values.ackCodeOfConduct === true,
    declaration: values.declaration === true,
  };
  ALL_DOCUMENTS.forEach(doc => {
    if (!documentApplies(doc, values.ownVehicle)) {
      return;
    }
    if (doc.expiryField && values[doc.expiryField]) {
      body[doc.expiryField] = values[doc.expiryField];
    }
    doc.uploads.forEach(field => {
      const fileId = values.files?.[field]?.fileId;
      if (fileId) {
        body.files[field] = fileId;
      }
    });
  });
  return body;
};
